import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import {
  type PublishedProtocol,
  type PublishedProtocolFood,
  type PublishedProtocolRule,
} from '../../utils/publishedProtocolsStore';
import {
  loadProtocolTemplatesWithDetails,
  type ProtocolTemplateWithDetails,
} from '../../utils/loadDataFromContract';
import {
  FoodDialog,
  RuleDialog,
  TemplatePickerDialog,
} from './ProtocolPanelDialogs';

interface ProtocolExpandedPanelProps {
  protocol: PublishedProtocol;
  onUpdate: (id: string, updates: Partial<PublishedProtocol>) => void;
}

// ─── Main Panel ──────────────────────────────────────────────────────────────

export const ProtocolExpandedPanel = ({ protocol, onUpdate }: ProtocolExpandedPanelProps) => {
  const templateNotes = Array.isArray(protocol.templateNotes)
    ? protocol.templateNotes
    : typeof protocol.templateNotes === 'string'
      ? [protocol.templateNotes]
      : [];
  const classifications = Array.isArray(protocol.classifications)
    ? protocol.classifications
    : [];
  const rules = Array.isArray(protocol.rules) ? protocol.rules : [];

  // food dialogs
  const [foodDialogOpen, setFoodDialogOpen] = useState(false);
  const [editingFood, setEditingFood] = useState<PublishedProtocolFood | undefined>(undefined);

  // rule dialogs
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<PublishedProtocolRule | undefined>(undefined);

  // template picker
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  // template details expand
  const [templateExpanded, setTemplateExpanded] = useState(false);
  const [templateDetails, setTemplateDetails] = useState<ProtocolTemplateWithDetails | null>(null);
  const templateMealGuidelines = Array.isArray(templateDetails?.mealGuidelines)
    ? (templateDetails?.mealGuidelines ?? [])
    : [];
  const templateTimingRules = Array.isArray(templateDetails?.timingRules)
    ? (templateDetails?.timingRules ?? [])
    : [];

  // personalization notes
  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');

  useEffect(() => {
    const all = loadProtocolTemplatesWithDetails();
    const found = all.find((t) => t.id === protocol.templateId) ?? null;
    setTemplateDetails(found);
  }, [protocol.templateId]);

  const handleAddNote = () => {
    const trimmed = newNoteText.trim();
    if (!trimmed) return;
    const next = [...templateNotes, trimmed];
    onUpdate(protocol.id, { templateNotes: next });
    setNewNoteText('');
    setAddNoteDialogOpen(false);
  };

  const handleDeleteNote = (index: number) => {
    const next = templateNotes.filter((_, i) => i !== index);
    onUpdate(protocol.id, { templateNotes: next.length ? next : undefined });
  };

  // ── Food handlers ────────────────────────────────────────────────────────

  const openAddFood = () => {
    setEditingFood(undefined);
    setFoodDialogOpen(true);
  };

  const openEditFood = (food: PublishedProtocolFood) => {
    setEditingFood(food);
    setFoodDialogOpen(true);
  };

  const handleSaveFood = (food: PublishedProtocolFood) => {
    const next = editingFood
      ? classifications.map((f) => (f.id === food.id ? food : f))
      : [...classifications, food];
    onUpdate(protocol.id, { classifications: next });
  };

  const handleDeleteFood = (foodId: string) => {
    const next = classifications.filter((f) => f.id !== foodId);
    onUpdate(protocol.id, { classifications: next });
  };

  // ── Rule handlers ─────────────────────────────────────────────────────────

  const openAddRule = () => {
    setEditingRule(undefined);
    setRuleDialogOpen(true);
  };

  const openEditRule = (rule: PublishedProtocolRule) => {
    setEditingRule(rule);
    setRuleDialogOpen(true);
  };

  const handleSaveRule = (rule: PublishedProtocolRule) => {
    const next = editingRule
      ? rules.map((r) => (r.id === rule.id ? rule : r))
      : [...rules, rule];
    onUpdate(protocol.id, { rules: next });
  };

  const handleDeleteRule = (ruleId: string) => {
    const next = rules.filter((r) => r.id !== ruleId);
    onUpdate(protocol.id, { rules: next });
  };

  // ── Template handler ──────────────────────────────────────────────────────

  const handleSelectTemplate = (templateId: string) => {
    onUpdate(protocol.id, { templateId });
  };

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
      <Typography variant="subtitle2" gutterBottom>
        Protocol Details
      </Typography>

      {/* Patient info */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary">
            Patient
          </Typography>
          <Typography variant="body2">{protocol.patientName}</Typography>
        </Grid>

        {/* Template section */}
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              Template
            </Typography>
            <Chip size="small" label={protocol.templateId} />
            <Tooltip title="Change template">
              <IconButton size="small" onClick={() => setTemplatePickerOpen(true)}>
                <SwapHorizIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={templateExpanded ? 'Collapse template details' : 'Expand template details'}>
              <IconButton size="small" onClick={() => setTemplateExpanded((p) => !p)}>
                {templateExpanded ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
          <Collapse in={templateExpanded} timeout="auto" unmountOnExit>
            {templateDetails ? (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {templateDetails.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {templateDetails.description}
                </Typography>
                <Typography variant="caption" display="block" gutterBottom>
                  <strong>Population:</strong> {templateDetails.targetPopulation}
                </Typography>
                <Typography variant="caption" display="block" gutterBottom>
                  <strong>Glucose Targets</strong> — Fasting:{' '}
                  {templateDetails.glucoseTargets.fasting} · Post-meal:{' '}
                  {templateDetails.glucoseTargets.postMeal} · Bedtime:{' '}
                  {templateDetails.glucoseTargets.bedtime}
                </Typography>
                {templateMealGuidelines.length > 0 && (
                  <>
                    <Typography variant="caption" fontWeight={600} display="block" mt={0.5}>
                      Meal Guidelines
                    </Typography>
                    {templateMealGuidelines.map((g, i) => (
                      <Typography key={i} variant="caption" display="block">
                        • {g}
                      </Typography>
                    ))}
                  </>
                )}
                {templateTimingRules.length > 0 && (
                  <>
                    <Typography variant="caption" fontWeight={600} display="block" mt={0.5}>
                      Timing Rules
                    </Typography>
                    {templateTimingRules.map((r, i) => (
                      <Typography key={i} variant="caption" display="block">
                        • {r}
                      </Typography>
                    ))}
                  </>
                )}
                <Box sx={{ mt: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                    <Typography variant="caption" fontWeight={600} display="block">
                      Personalization Notes
                    </Typography>
                    <Tooltip title="Add note">
                      <IconButton size="small" onClick={() => { setNewNoteText(''); setAddNoteDialogOpen(true); }}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  {templateNotes.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      No personalization notes yet. Click + to add one.
                    </Typography>
                  ) : (
                    <Stack spacing={0.5}>
                      {templateNotes.map((note, i) => (
                        <Stack key={i} direction="row" alignItems="flex-start" spacing={0.5}>
                          <Typography variant="caption" sx={{ flex: 1, pt: 0.25 }}>
                            {i + 1}. {note}
                          </Typography>
                          <Tooltip title="Remove note">
                            <IconButton size="small" color="error" onClick={() => handleDeleteNote(i)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No template details found.
              </Typography>
            )}
          </Collapse>
        </Grid>
      </Grid>

      {/* Food Classifications */}
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography variant="body2" fontWeight={600}>
          Food Classifications ({classifications.length})
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={openAddFood}>
          Add
        </Button>
      </Stack>
      {classifications.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No food classifications.
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Food</TableCell>
                <TableCell>Classification</TableCell>
                <TableCell>Portion</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {classifications.map((food) => (
                <TableRow key={food.id}>
                  <TableCell>{food.customFoodName}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={food.classification}
                      color={
                        food.classification === 'GREEN'
                          ? 'success'
                          : food.classification === 'YELLOW'
                          ? 'warning'
                          : 'error'
                      }
                    />
                  </TableCell>
                  <TableCell>{food.portion}</TableCell>
                  <TableCell>{food.notes || '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit food">
                      <IconButton size="small" onClick={() => openEditFood(food)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove food">
                      <IconButton size="small" color="error" onClick={() => handleDeleteFood(food.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Rules */}
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography variant="body2" fontWeight={600}>
          Rules ({rules.length})
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={openAddRule}>
          Add
        </Button>
      </Stack>
      {rules.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No protocol rules.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {rules.map((rule) => (
            <Paper key={rule.id} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                    <Chip size="small" label={rule.ruleType} color="primary" />
                    <Chip
                      size="small"
                      label={rule.isActive ? 'Active' : 'Inactive'}
                      color={rule.isActive ? 'success' : 'default'}
                    />
                  </Stack>
                  <Typography variant="body2">{rule.description}</Typography>
                </Box>
                <Tooltip title="Edit rule">
                  <IconButton size="small" onClick={() => openEditRule(rule)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove rule">
                  <IconButton size="small" color="error" onClick={() => handleDeleteRule(rule.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Dialogs */}
      <FoodDialog
        open={foodDialogOpen}
        initial={editingFood}
        onClose={() => setFoodDialogOpen(false)}
        onSave={handleSaveFood}
      />
      <RuleDialog
        open={ruleDialogOpen}
        initial={editingRule}
        onClose={() => setRuleDialogOpen(false)}
        onSave={handleSaveRule}
      />
      <TemplatePickerDialog
        open={templatePickerOpen}
        currentTemplateId={protocol.templateId}
        onClose={() => setTemplatePickerOpen(false)}
        onSelect={handleSelectTemplate}
      />

      {/* Add Personalization Note Dialog */}
      <Dialog open={addNoteDialogOpen} onClose={() => setAddNoteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Personalization Note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 1 }}
            label="Note"
            placeholder="e.g. Patient prefers smaller meals — reduce carb targets by 10g…"
            value={newNoteText}
            onChange={(e) => setNewNoteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddNoteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddNote} disabled={!newNoteText.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
